import { Chat, InitDataResponse, User } from "./model"

// This flag is deliberately unavailable in production builds.
export const isMockMode = process.env.NODE_ENV === "development" && process.env.REACT_APP_MOCK === "true"
export type MockRole = "user" | "admin"

export const canFilterChat = (chat: Chat, data: InitDataResponse, viewerId?: string) => {
  if (typeof chat.is_admin === "boolean") return chat.is_admin
  if (typeof data.is_admin === "boolean") return data.is_admin
  // Legacy API exposes other members only to chat administrators.
  const ownId = data.user_id || viewerId
  return (
    chat.users.length > 1 || Boolean(ownId && chat.users.some((user) => user.uid && String(user.uid) !== String(ownId)))
  )
}

export const createMockData = (role: MockRole): InitDataResponse => ({
  user_id: "1001",
  is_admin: role === "admin",
  common_name: "Александр",
  chats: [
    {
      id: -100101,
      title: "Футбол по четвергам",
      is_admin: role === "admin",
      users: [
        { uid: "1001", title: "Александр Смирнов", name: "Саша", username: "sasha_s", rank: "4" },
        { uid: "1002", title: "Михаил Волков", name: "Миша", username: "volk", rank: "gk" },
        { uid: "1003", title: "Даниил Козлов", name: "Даня", username: null, rank: "?" },
        { uid: "1004", title: "Иван Петров", name: "Ваня", username: "ivan_p", rank: "3" },
      ].filter((user) => role === "admin" || user.uid === "1001"),
      ...(role === "admin" ? { paid: { "2": ["1002"], "4": ["1001", "1004"] } } : {}),
    },
    {
      id: -100102,
      title: "Команда выходного дня",
      is_admin: role === "admin",
      users: [
        { uid: "1001", title: "Александр Смирнов", name: "Алекс", rank: "4" },
        { uid: "1005", title: "Артём Орлов", name: "Артём", rank: "5" },
      ].filter((user) => role === "admin" || user.uid === "1001"),
      ...(role === "admin" ? { paid: {} } : {}),
    },
  ],
})

export const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"]

/** JSON для буфера обмена: uid + имена, чтобы список было удобно читать и переносить между днями */
export const exportPaidJson = (uids: string[], users: User[]) =>
  JSON.stringify(
    uids.map((uid) => {
      const user = users.find((item) => item.uid === uid)
      return { uid, name: user?.name || user?.title || "" }
    }),
    null,
    2,
  )

/**
 * Разбор импортируемого JSON: массив uid (строки/числа) или объектов с полем uid,
 * либо объект с полем uids/users. Бросает ошибку с понятным сообщением.
 */
export const parsePaidJson = (text: string): string[] => {
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    throw new Error("Не удалось разобрать JSON. Проверьте формат.")
  }
  if (parsed && !Array.isArray(parsed) && typeof parsed === "object") {
    const wrapper = parsed as { uids?: unknown; users?: unknown }
    parsed = wrapper.uids ?? wrapper.users
  }
  if (!Array.isArray(parsed)) throw new Error("Ожидается массив участников.")
  const uids = parsed.map((item) => {
    const value = item && typeof item === "object" ? (item as { uid?: unknown; id?: unknown }).uid ?? item.id : item
    if ((typeof value !== "string" && typeof value !== "number") || !/^\d{1,20}$/.test(String(value).trim())) {
      throw new Error(`Некорректный участник: ${JSON.stringify(item)}`)
    }
    return String(value).trim()
  })
  return Array.from(new Set(uids))
}

export async function appRequest(path: string, hash: string | undefined, params: object = {}) {
  if (isMockMode) return undefined // Mock edits live in React state and never reach the backend.
  const host = process.env.REACT_APP_API_URL
  if (!host) throw new Error("Не задан адрес API. Проверьте REACT_APP_API_URL.")
  const response = await fetch(`${host}/api/app/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    ...(process.env.NODE_ENV === "development" ? { credentials: "include" as const } : {}),
    body: JSON.stringify({ hash, ...params }),
  })
  if (!response.ok) throw new Error("Не удалось связаться с сервером. Попробуйте ещё раз.")
  if (path === "init") return response.json() as Promise<InitDataResponse>
  return undefined
}
